import React, {useState, useEffect} from 'react';
import {Button, Tooltip, message, Spin} from 'antd';
import {
    CheckCircleOutlined,
    CopyOutlined,
    DollarCircleOutlined,
    ShareAltOutlined,
    PercentageOutlined,
    SafetyCertificateOutlined,
    UserAddOutlined,
} from '@ant-design/icons';
import partnerService from '../../services/partnerService';
import {useSelector} from 'react-redux';
import {RootState} from '../../store/rootReducer';
import {getCookie} from 'typescript-cookie';
import {showSubscriptionModal} from '../../components/molecules/Modals/subscription/component';
import {trackEvent} from '../../analytics/posthog';
import './style.css';

const earningHighlights = [
    {
        icon: <PercentageOutlined/>,
        value: '40%',
        label: 'of every subscription',
        text: 'Earn recurring subscription revenue from anyone who signs up through your link.'
    },
    {
        icon: <DollarCircleOutlined/>,
        value: '10%',
        label: 'of advertiser click budget spend',
        text: 'Earn a share of campaign spend from advertisers you personally refer.'
    },
    {
        icon: <SafetyCertificateOutlined/>,
        value: 'Automatic',
        label: 'tracking and payouts',
        text: 'Promotify attributes referrals and commissions while your users stay active.'
    }
];

const exampleRows = [
    {spend: '$500 ad spend', payout: '$50'},
    {spend: '$5,000 ad spend', payout: '$500'}
];

const steps = [
    {
        title: 'Share your unique link',
        text: 'Send your link to advertisers, promoters, or future partners anywhere.'
    },
    {
        title: 'Users sign up and subscribe',
        text: 'They create an account and become active subscribers on Promotify.'
    },
    {
        title: 'You earn recurring commission',
        text: 'We track the source, calculate earnings, and pay you automatically.'
    }
];

type InviteRole = {
    roleType: string;
    isActive?: boolean;
};

type InviteUser = {
    roles?: InviteRole[];
};

const InviteUsersPage: React.FC = () => {
    const [inviteLink, setInviteLink] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const currentUser = useSelector((state: RootState) => state.auth.user) as InviteUser | null;
    const role = getCookie('roleType') || currentUser?.roles?.[0]?.roleType;
    const currentRole = currentUser?.roles?.find((r) => r.roleType === role);
    const isActivePartner = Boolean(currentRole?.isActive);

    useEffect(() => {
        trackEvent('partner_invite_page_viewed', {role});
    }, [role]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                if (currentRole?.isActive) {
                    const result = await partnerService.inviteUsers();
                    setInviteLink(result.data.recruitmentLink);
                } else {
                    setInviteLink(null);
                }
            } catch (err: unknown) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to retrieve invite link.';
                message.error(errorMessage);
            } finally {
                setLoading(false);
            }
        })();
    }, [currentRole]);

    const handleCopy = () => {
        if (!inviteLink) return;

        navigator.clipboard
            .writeText(inviteLink)
            .then(() => {
                trackEvent('partner_referral_link_copied', {
                    role,
                    referral_link_present: true
                });
                message.success('Link copied to clipboard!');
            })
            .catch(() => message.error('Failed to copy link.'));
    };

    return (
        <div className="inviteUsersPage">
            <section className="inviteUsersHero">
                <div className="inviteUsersDecor inviteUsersDecorRing"/>
                <div className="inviteUsersDecor inviteUsersDecorPlus">+</div>
                <div className="inviteUsersDecor inviteUsersDecorDot"/>

                <div className="inviteUsersHeroGrid">
                    <div className="inviteUsersHeroCopy">
                        <span className="inviteUsersHeroKicker">Partner invite center</span>
                        <h1>Earn recurring income with PromotifyAI.</h1>
                        <p>
                            Refer advertisers, promoters, and partners to PromotifyAI and earn 40% recurring commission
                            on every subscription, plus 10% of advertiser campaign spend you bring to the platform.
                        </p>
                        {isActivePartner && (
                            <div className="inviteUsersHeroActions">
                                <Button type="primary" icon={<CopyOutlined/>} onClick={handleCopy} disabled={!inviteLink}>
                                    Copy invite link
                                </Button>
                            </div>
                        )}
                        <small>Build your partner network, track your earnings, and withdraw anytime.</small>
                    </div>

                    <div className="inviteUsersHeroImage">
                        <div className="inviteUsersHeroSnapshot">
                            <strong>Earn While They Advertise!</strong>
                            <ul>
                                <li>40% of subscription revenue</li>
                                <li>10% of ad spend from advertisers you refer</li>
                                <li>Fully automatic tracking and payouts</li>
                            </ul>
                            <strong>Let us talk REAL NUMBERS!</strong>
                            <ul>
                                <li>$500 spent in ads - <b>$50 to you</b></li>
                                <li>$5,000 spent in ads - <b>$500 to you</b></li>
                            </ul>
                        </div>
                        <img
                            src="/assets/images/partner-guide/partner-main.png"
                            alt="Partner referral commission illustration"
                        />
                    </div>
                </div>

                <div className="inviteUsersHighlights">
                    {earningHighlights.map((item) => (
                        <article className="inviteUsersHighlight" key={item.value}>
                            <div className="inviteUsersIcon">{item.icon}</div>
                            <div>
                                <strong>{item.value}</strong>
                                <span>{item.label}</span>
                                <p>{item.text}</p>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            <section className="inviteUsersReferralSection">
                <div className="inviteUsersNarrow">
                    <span className="inviteUsersSectionKicker">Your link</span>
                    <h2>Share once. Tracking keeps working in the background.</h2>
                </div>

                <div className="inviteUsersGrid">
                    <article className="inviteUsersPanel">
                        <h2>See how it adds up</h2>
                        <p>Real examples of what you can earn from advertiser spend.</p>
                        <div className="inviteUsersExamples">
                            {exampleRows.map((row) => (
                                <div className="inviteUsersExampleRow" key={row.spend}>
                                    <span><DollarCircleOutlined/></span>
                                    <strong>{row.spend}</strong>
                                    <b>=</b>
                                    <strong className="inviteUsersPayout">{row.payout}</strong>
                                    <em>to you</em>
                                </div>
                            ))}
                        </div>
                        <small>Example based on 10% of advertiser click budget spend.</small>
                    </article>

                    <article className="inviteUsersPanel inviteUsersLinkPanel">
                        <h2>Your referral link</h2>
                        <p>Share your unique link and start earning.</p>

                        {loading && !inviteLink ? (
                            <div className="inviteUsersLoading">
                                <Spin tip="Generating your link..."/>
                            </div>
                        ) : isActivePartner ? (
                            <>
                                <div className="inviteUsersLinkBox">
                                    <Tooltip title={inviteLink || undefined}>
                                        <span
                                            className="inviteUsersLinkValue"
                                            onClick={handleCopy}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    handleCopy();
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            aria-label="Copy referral link"
                                        >
                                            {inviteLink || 'Your link is being prepared'}
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Copy link">
                                        <Button
                                            className="inviteUsersCopyButton"
                                            type="primary"
                                            icon={<CopyOutlined/>}
                                            onClick={handleCopy}
                                            aria-label="Copy referral link"
                                        />
                                    </Tooltip>
                                </div>
                            </>
                        ) : (
                            <div className="inviteUsersSubscribe">
                                <p>
                                    This feature is available only to subscribed partners. Subscribe to unlock your invite
                                    link and start earning from your network.
                                </p>
                                <Button type="primary" onClick={showSubscriptionModal}>Subscribe now</Button>
                            </div>
                        )}
                    </article>
                </div>
            </section>

            <section className="inviteUsersSteps">
                <div className="inviteUsersNarrow">
                    <span className="inviteUsersSectionKicker">How it works</span>
                    <h2>How your invite link turns into earnings</h2>
                </div>
                <div className="inviteUsersStepList">
                    {steps.map((step, index) => (
                        <React.Fragment key={step.title}>
                            <article className="inviteUsersStep">
                                <div>
                                    <span>{index + 1}</span>
                                    <h3>{step.title}</h3>
                                    <p>{step.text}</p>
                                </div>
                            </article>
                            {index < steps.length - 1 && <span className="inviteUsersStepArrow" aria-hidden="true"/>}
                        </React.Fragment>
                    ))}
                </div>
                <div className="inviteUsersAssurance">
                    <CheckCircleOutlined/>
                    <span>No extra work. No invoices. Just share and earn.</span>
                </div>
            </section>
        </div>
    );
};

export default InviteUsersPage;
